import { capitalize } from "lodash";
import { toFormat } from "@/lib/dateHelpers";
import {
  Person,
  PgmRequirementStatus,
  PgmProgramRequirement,
  PgmTestScore,
  EducationPgmProgram,
  ProgramTypeName
} from "@/types";
import { deduplicateTests } from "@/features/person/components/PGMProgramTable/utils";
import type { TagTooltip } from "@/components/Tag/Tag";
import type { Path } from "@/router";
import { isProgressVersionSupported } from "./pgm-levels";

export interface RequirementRow {
  id: string;
  name: string;
  completionDate: string | null;
  status: string;
  statusType: "success" | "warn" | "error" | "surface" | "information";
  key?: string;
  source?: "progress" | "progress-overlay" | "history";
  statusTooltip?: TagTooltip;
  /** Member 360 membership application id when this row can deep-link to application details. */
  membershipApplicationId?: string;
  /** Route to open when the requirement row is clicked. */
  detailPath?: Path;
  /** In-page section id to scroll to when the requirement row is clicked. */
  scrollToSectionId?: string;
}

export function requirementRowHasDestination(row: RequirementRow): boolean {
  return Boolean(row.membershipApplicationId || row.detailPath || row.scrollToSectionId);
}

export function requirementRowScrollsInPage(row: RequirementRow): boolean {
  return Boolean(row.scrollToSectionId) && !row.membershipApplicationId && !row.detailPath;
}

const STATUS_TAG_COLORS: Record<string, RequirementRow["statusType"]> = {
  Completed: "success",
  Complete: "success",
  Passed: "success",
  Qualified: "information",
  Attending: "warn",
  Attended: "success",
  Approved: "success",
  "In Progress": "warn",
  Pending: "warn",
  Registered: "warn",
  Submitted: "warn",
  Denied: "error",
  Failed: "error",
  Expired: "error",
  Open: "information",
  Locked: "surface",
  Incomplete: "surface",
  "Not Started": "surface",
  "Not Attempted": "surface"
};

export function getStatusTagColor(status: string): RequirementRow["statusType"] {
  return STATUS_TAG_COLORS[status] || "surface";
}

const COMPLETE_REQUIREMENT_STATUSES = new Set(["Complete", "Completed", "Passed", "Qualified", "Attended", "Approved"]);

export function isRequirementRowSatisfied(row: RequirementRow): boolean {
  return COMPLETE_REQUIREMENT_STATUSES.has(row.status);
}

export function getCompletionLabel(
  isCompleted: boolean,
  completionStatus: string,
  options?: { archived?: boolean }
): string {
  if (!isCompleted) return options?.archived ? "Incomplete" : "In Progress";
  switch (completionStatus) {
    case "ATND":
      return "Attended";
    case "COMP":
      return "Completed";
    case "APPR":
      return "Approved";
    default:
      return "Complete";
  }
}

function formatRequirementStatus(status?: string): string {
  if (!status) return "Not Started";
  return status
    .split("_")
    .map((word) => capitalize(word))
    .join(" ");
}

function mapProgressRequirement(
  req: PgmRequirementStatus,
  index: number,
  source: "progress" | "progress-overlay" = "progress"
): RequirementRow {
  const status = formatRequirementStatus(req.status);

  return {
    id: `progress-${index}`,
    key: req.key,
    name: req.name,
    completionDate: req.completionDate ? toFormat(req.completionDate) : null,
    status,
    statusType: getStatusTagColor(status),
    source
  };
}

function mapHistoryRequirement(req: PgmProgramRequirement, index: number): RequirementRow {
  const status = getCompletionLabel(req.isCompleted, req.completionStatus, { archived: req.archived });

  return {
    id: `history-${index}`,
    key: req.requirementDefinition?.requireType?.toLowerCase(),
    name: req.requirementDefinition?.description || "Unknown requirement",
    completionDate: req.completionDate ? toFormat(req.completionDate) : null,
    status,
    statusType: getStatusTagColor(status),
    source: "history"
  };
}

function mapTestToRequirementRow(test: PgmTestScore, index: number): RequirementRow {
  const status = test.isPassed ? "Passed" : test.isFailed ? "Failed" : "Not Attempted";

  return {
    id: `test-${test.testId}-${index}`,
    name: test.testDefinition?.description || test.testId,
    completionDate: test.testDate ? toFormat(test.testDate) : null,
    status,
    statusType: getStatusTagColor(status)
  };
}

function buildTestRequirementRows(levelData: EducationPgmProgram): RequirementRow[] {
  const levelTests = (levelData.testHistory || []).filter((test) => test.levelCode === levelData.levelCode);

  return deduplicateTests(levelTests).map(mapTestToRequirementRow);
}

export function filterAndSortRequirements<T extends { levelCode: string; completionDate?: string | null }>(
  requirements: T[],
  levelCode: string
): T[] {
  return requirements
    .filter((req) => req.levelCode === levelCode)
    .sort((a, b) => {
      if (!a.completionDate && !b.completionDate) return 0;
      if (!a.completionDate) return -1;
      if (!b.completionDate) return 1;
      return new Date(b.completionDate).getTime() - new Date(a.completionDate).getTime();
    });
}

interface BuildLevelRequirementsOptions {
  requirementKeys?: string[];
  /** Qualifying Level table only — keep shared L0 callers on append order. */
  placeTestsBeforeApplicationGates?: boolean;
}

function isApplicationOrPurchaseRow(row: RequirementRow): boolean {
  return (
    row.key === "purchase" ||
    row.key === "application" ||
    /^(affiliate|associate)?\s*application$/i.test(row.name.trim()) ||
    /^Purchase Level/i.test(row.name)
  );
}

function placeLevel3ApplicationLast(rows: RequirementRow[], levelNumber: number): RequirementRow[] {
  if (levelNumber !== 3 || !rows.some((row) => row.key === "enrollment")) return rows;

  return [...rows.filter((row) => row.key !== "enrollment"), ...rows.filter((row) => row.key === "enrollment")];
}

function mergeRequirementAndTestRows(
  requirementRows: RequirementRow[],
  testRows: RequirementRow[],
  levelNumber: number,
  options?: BuildLevelRequirementsOptions
): RequirementRow[] {
  let rows = requirementRows;

  if (testRows.length > 0 && levelNumber === 0 && options?.placeTestsBeforeApplicationGates) {
    const gateIndex = requirementRows.findIndex(isApplicationOrPurchaseRow);
    if (gateIndex !== -1) {
      rows = [...requirementRows.slice(0, gateIndex), ...testRows, ...requirementRows.slice(gateIndex)];
    } else {
      rows = [...requirementRows, ...testRows];
    }
  } else if (testRows.length > 0) {
    rows = [...requirementRows, ...testRows];
  }

  return placeLevel3ApplicationLast(rows, levelNumber);
}

function progressOverlayKeys(levelNumber: number): string[] {
  if (levelNumber === 0) return ["application"];
  if (levelNumber === 3) return ["enrollment"];
  return [];
}

function shouldOverlayProgressApplications(person: Person): boolean {
  const membershipType = person.latestProgram?.programType?.name;
  if (membershipType === ProgramTypeName.Student) return false;
  if (!membershipType && person.pgmProgressStatus?.programType === ProgramTypeName.Student) return false;
  return true;
}

function buildProgressOverlayRows(person: Person, levelNumber: number, requirementKeys?: string[]): RequirementRow[] {
  const overlayKeys = progressOverlayKeys(levelNumber);
  if (overlayKeys.length === 0 || !shouldOverlayProgressApplications(person)) return [];

  const level = person.pgmProgressStatus?.levels.find((item) => item.numeral === levelNumber);
  if (!level) return [];

  return level.requirements
    .filter((requirement) => overlayKeys.includes(requirement.key))
    .filter((requirement) => !requirementKeys || requirementKeys.includes(requirement.key))
    .map((requirement, index) => mapProgressRequirement(requirement, index, "progress-overlay"));
}

export function omitSyntheticMemberEnrollmentOverlay(
  rows: RequirementRow[],
  person: Person,
  levelNumber: number,
  options?: { applicationsResolved?: boolean }
): RequirementRow[] {
  if (levelNumber !== 3) return rows;
  if (options?.applicationsResolved === false) return rows;
  if (person.latestProgram?.programType?.name !== ProgramTypeName.Member) return rows;

  const hasLevel3History = (person.educationPgmProgramHistory ?? []).some((item) => item.level === 3);
  const hasLevel3CourseworkRows = rows.some((row) => row.key !== "enrollment");
  if (hasLevel3History || hasLevel3CourseworkRows) return rows;

  return rows.filter((row) => row.key !== "enrollment" || Boolean(row.membershipApplicationId));
}

function mergeProgressOverlayRows(
  requirementRows: RequirementRow[],
  progressRows: RequirementRow[],
  levelNumber: number
): RequirementRow[] {
  const rows = [...requirementRows];
  const newRows: RequirementRow[] = [];

  for (const progressRow of progressRows) {
    const existingIndex = rows.findIndex((row) => row.key === progressRow.key);
    if (existingIndex === -1) {
      newRows.push(progressRow);
    } else {
      rows[existingIndex] = progressRow;
    }
  }

  if (newRows.length === 0) return rows;
  if (levelNumber !== 0) return [...rows, ...newRows];

  const purchaseIndex = rows.findIndex((row) => row.key === "purchase" || /^Purchase Level/i.test(row.name));
  if (purchaseIndex === -1) return [...rows, ...newRows];
  return [...rows.slice(0, purchaseIndex), ...newRows, ...rows.slice(purchaseIndex)];
}

export function buildLevelRequirements(
  person: Person,
  levelNumber: number,
  options?: BuildLevelRequirementsOptions
): RequirementRow[] {
  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;
  const levelData = educationPgmProgramHistory.find((item) => item.level === levelNumber);
  const testRows = levelData ? buildTestRequirementRows(levelData) : [];
  const progressOverlayRows = buildProgressOverlayRows(person, levelNumber, options?.requirementKeys);

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const level = pgmProgressStatus.levels.find((l) => l.numeral === levelNumber);
    if (!level) return [];

    let reqs = options?.requirementKeys
      ? level.requirements.filter((r) => options.requirementKeys!.includes(r.key))
      : level.requirements;

    if (testRows.length > 0 && levelNumber === 3) {
      reqs = reqs.filter((r) => r.key !== "final_exam");
    }

    return mergeRequirementAndTestRows(
      reqs.map((requirement, index) => mapProgressRequirement(requirement, index)),
      testRows,
      levelNumber,
      options
    );
  }

  if (!levelData) return progressOverlayRows;

  const sorted = levelData.programRequirements
    ? filterAndSortRequirements(levelData.programRequirements, levelData.levelCode)
    : [];

  const requirementRows = options?.requirementKeys
    ? sorted
        .filter((r) => {
          const type = r.requirementDefinition?.requireType?.toLowerCase();
          return type && options.requirementKeys!.includes(type);
        })
        .map(mapHistoryRequirement)
    : sorted.map(mapHistoryRequirement);

  return mergeRequirementAndTestRows(
    mergeProgressOverlayRows(requirementRows, progressOverlayRows, levelNumber),
    testRows,
    levelNumber,
    options
  );
}
